'use client';

import React, { useState, useRef } from 'react';
import {
  Scan, Package, Hash, Calendar, User, Clock,
  AlertTriangle, CheckCircle2, ChevronRight, RotateCcw,
  Layers, Truck, FileText, Send, Search, Printer,
  MapPin, ShieldCheck
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChildItem {
  id: string;
  itemName: string;
  batchNumber: string;
  quantity: string;
  uom: string;
  mfgDate: string;
  expiryDate: string;
  shelfLifePct: number | null;
  rackLocation: string;
  grnId: string;
  status: 'ok' | 'expiring' | 'expired';
}

interface KitRecord {
  kitBarcode: string;
  kitId: string;
  kitName: string;
  completedAt: string;
  completedDate: string;
  operator: string;
  operatorId: string;
  device: string;
  totalItems: number;
  destination: string;
  dispatchStatus: 'ready' | 'dispatched';
  childItems: ChildItem[];
}

// ─── Mock Kit Data ─────────────────────────────────────────────────────────────

const KIT_DATABASE: Record<string, KitRecord> = {
  'KIT-20260612-001': {
    kitBarcode: 'KIT-20260612-001',
    kitId: 'KIT-20260612-001',
    kitName: 'Surgical Dressing Kit — Type A',
    completedAt: '09:42 AM',
    completedDate: '12 Jun 2026',
    operator: 'Prasun Sharma',
    operatorId: 'OPR-001',
    device: 'HH-01',
    totalItems: 5,
    destination: 'Ward 3 — General Surgery',
    dispatchStatus: 'ready',
    childItems: [
      {
        id: 'CI-001',
        itemName: 'Non Woven Tape 75mm x 250cm',
        batchNumber: 'BAT-2026-001',
        quantity: '2',
        uom: 'ROLL',
        mfgDate: '01 Jan 2026',
        expiryDate: '01 Jun 2027',
        shelfLifePct: 82,
        rackLocation: 'A-01-02-B-05',
        grnId: 'GRN-20260612-001',
        status: 'ok',
      },
      {
        id: 'CI-002',
        itemName: 'Gauze Swab 10x10cm Sterile',
        batchNumber: 'BAT-2026-007',
        quantity: '5',
        uom: 'PCS',
        mfgDate: '15 Feb 2026',
        expiryDate: '30 Jun 2026',
        shelfLifePct: 42,
        rackLocation: 'A-01-02-B-03',
        grnId: 'GRN-20260611-087',
        status: 'expiring',
      },
      {
        id: 'CI-003',
        itemName: 'Surgical Gloves Size M',
        batchNumber: 'BAT-2026-006',
        quantity: '2',
        uom: 'PAIR',
        mfgDate: '10 Mar 2026',
        expiryDate: '15 Mar 2027',
        shelfLifePct: 75,
        rackLocation: 'A-01-01-A-03',
        grnId: 'GRN-20260612-002',
        status: 'ok',
      },
      {
        id: 'CI-004',
        itemName: 'BP Scalpel Handle Size 4',
        batchNumber: 'AUTO-345604',
        quantity: '1',
        uom: 'PCS',
        mfgDate: '05 Apr 2026',
        expiryDate: 'No Expiry',
        shelfLifePct: null,
        rackLocation: 'C-03-01-B-02',
        grnId: 'GRN-20260612-002',
        status: 'ok',
      },
      {
        id: 'CI-005',
        itemName: 'Mayo S Trolley Cover Small',
        batchNumber: 'BAT-2026-003',
        quantity: '1',
        uom: 'PCS',
        mfgDate: '20 Jan 2026',
        expiryDate: '01 Sep 2028',
        shelfLifePct: 91,
        rackLocation: 'B-02-03-A-01',
        grnId: 'GRN-20260612-001',
        status: 'ok',
      },
    ],
  },
  'KIT-20260611-045': {
    kitBarcode: 'KIT-20260611-045',
    kitId: 'KIT-20260611-045',
    kitName: 'Wound Care Kit — Standard',
    completedAt: '02:15 PM',
    completedDate: '11 Jun 2026',
    operator: 'Anita Desai',
    operatorId: 'OPR-003',
    device: 'HH-02',
    totalItems: 3,
    destination: 'OPD — Dressing Room',
    dispatchStatus: 'ready',
    childItems: [
      {
        id: 'CI-101',
        itemName: 'Adhesive Bandage 25mm x 72mm',
        batchNumber: 'BAT-2026-011',
        quantity: '10',
        uom: 'PCS',
        mfgDate: '01 Mar 2026',
        expiryDate: '01 Mar 2028',
        shelfLifePct: 88,
        rackLocation: 'A-02-01-A-01',
        grnId: 'GRN-20260610-033',
        status: 'ok',
      },
      {
        id: 'CI-102',
        itemName: 'Antiseptic Wipes 70% IPA',
        batchNumber: 'BAT-2026-009',
        quantity: '3',
        uom: 'PCS',
        mfgDate: '10 Feb 2026',
        expiryDate: '10 Feb 2027',
        shelfLifePct: 68,
        rackLocation: 'B-01-02-A-02',
        grnId: 'GRN-20260610-033',
        status: 'ok',
      },
      {
        id: 'CI-103',
        itemName: 'Sterile Saline 0.9% 100ml',
        batchNumber: 'BAT-2026-014',
        quantity: '1',
        uom: 'BTL',
        mfgDate: '05 Jan 2026',
        expiryDate: '05 Jan 2027',
        shelfLifePct: 55,
        rackLocation: 'D-01-01-A-01',
        grnId: 'GRN-20260609-021',
        status: 'ok',
      },
    ],
  },
};

// ─── Shelf life bar ───────────────────────────────────────────────────────────

function ShelfLifeBar({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="text-2xs text-muted-foreground italic">No Expiry</span>;
  const color = pct >= 80 ? 'bg-primary' : pct >= 50 ? 'bg-accent' : 'bg-red-400';
  const textColor = pct >= 80 ? 'text-primary' : pct >= 50 ? 'text-accent' : 'text-red-400';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden min-w-[48px]">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-2xs font-bold tabular-nums ${textColor}`}>{pct}%</span>
    </div>
  );
}

// ─── Challan Preview Modal ────────────────────────────────────────────────────

interface ChallanModalProps {
  kit: KitRecord;
  challanNo: string;
  dispatchedAt: string;
  onClose: () => void;
  onPrint: () => void;
}

function ChallanModal({ kit, challanNo, dispatchedAt, onClose, onPrint }: ChallanModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-card border border-border rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Modal header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileText size={16} className="text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-foreground">Dispatch Challan</h2>
              <p className="text-xs text-muted-foreground font-mono">{challanNo}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onPrint}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
            >
              <Printer size={13} />
              Print / Save PDF
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              Close
            </button>
          </div>
        </div>

        {/* Challan content */}
        <div id="challan-print-area" className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Org header */}
          <div className="flex items-start justify-between pb-4 border-b border-border">
            <div>
              <p className="text-base font-bold text-foreground">WMS_Pro Warehouse</p>
              <p className="text-xs text-muted-foreground">Central Medical Stores · Dispatch Division</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold text-foreground">DISPATCH CHALLAN</p>
              <p className="text-xs font-mono text-primary mt-0.5">{challanNo}</p>
              <p className="text-2xs text-muted-foreground mt-0.5">{dispatchedAt}</p>
            </div>
          </div>

          {/* Kit & dispatch info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-muted/40 px-3 py-2.5 space-y-1.5">
              <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide">Kit Details</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Layers size={11} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-xs font-semibold text-foreground">{kit.kitName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Hash size={11} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-xs font-mono text-foreground">{kit.kitId}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Package size={11} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-xs text-foreground">{kit.totalItems} items</span>
                </div>
              </div>
            </div>
            <div className="rounded-lg bg-muted/40 px-3 py-2.5 space-y-1.5">
              <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide">Dispatch Info</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <User size={11} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-xs text-foreground">{kit.operator} ({kit.operatorId})</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={11} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-xs text-foreground">{dispatchedAt}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin size={11} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-xs text-foreground">{kit.destination}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Item manifest table */}
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="px-3 py-2 bg-muted/30 border-b border-border">
              <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide">Item Manifest</p>
            </div>
            {/* Table header */}
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/20 border-b border-border text-2xs font-semibold text-muted-foreground uppercase tracking-wide">
              <div className="col-span-4">Item Name</div>
              <div className="col-span-2">Batch No.</div>
              <div className="col-span-1 text-center">Qty</div>
              <div className="col-span-2">Mfg Date</div>
              <div className="col-span-2">Expiry Date</div>
              <div className="col-span-1 text-right">Status</div>
            </div>
            <div className="divide-y divide-border">
              {kit.childItems.map((item, idx) => (
                <div key={item.id} className="grid grid-cols-12 gap-2 px-3 py-2.5 items-center">
                  <div className="col-span-4">
                    <p className="text-xs font-medium text-foreground leading-tight">{item.itemName}</p>
                    <p className="text-2xs text-muted-foreground font-mono mt-0.5">{item.uom}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs font-mono text-foreground">{item.batchNumber}</span>
                  </div>
                  <div className="col-span-1 text-center">
                    <span className="text-xs font-bold text-foreground">{item.quantity}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs text-foreground">{item.mfgDate}</span>
                  </div>
                  <div className="col-span-2">
                    <span className={`text-xs font-medium ${item.status === 'expiring' ? 'text-accent' : item.status === 'expired' ? 'text-red-400' : 'text-foreground'}`}>
                      {item.expiryDate}
                    </span>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {item.status === 'expiring' ? (
                      <span className="text-2xs font-bold text-accent">⚠ Soon</span>
                    ) : item.status === 'expired' ? (
                      <span className="text-2xs font-bold text-red-400">Expired</span>
                    ) : (
                      <span className="text-2xs font-bold text-primary">OK</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Signature area */}
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="border-t border-border pt-3">
              <p className="text-2xs text-muted-foreground">Dispatched by (Operator)</p>
              <p className="text-xs font-semibold text-foreground mt-1">{kit.operator}</p>
              <p className="text-2xs text-muted-foreground">{kit.operatorId} · {kit.device}</p>
            </div>
            <div className="border-t border-border pt-3">
              <p className="text-2xs text-muted-foreground">Received by (Signature)</p>
              <div className="h-8 border-b border-dashed border-border mt-1" />
              <p className="text-2xs text-muted-foreground mt-1">Name &amp; Stamp</p>
            </div>
          </div>

          <p className="text-2xs text-muted-foreground text-center pt-1 border-t border-border">
            This is a system-generated dispatch challan. · WMS_Pro v2.6 · {challanNo}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Dispatched success overlay ───────────────────────────────────────────────

interface DispatchSuccessProps {
  kit: KitRecord;
  challanNo: string;
  dispatchedAt: string;
  onViewChallan: () => void;
  onScanNext: () => void;
}

function DispatchSuccess({ kit, challanNo, dispatchedAt, onViewChallan, onScanNext }: DispatchSuccessProps) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-5 py-10">
      <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
        <CheckCircle2 size={32} className="text-primary" />
      </div>
      <div className="text-center">
        <p className="text-lg font-bold text-foreground">Kit Dispatched!</p>
        <p className="text-sm text-muted-foreground mt-1">{kit.kitName}</p>
        <p className="text-xs font-mono text-muted-foreground mt-0.5">{kit.kitId}</p>
      </div>
      <div className="flex flex-col gap-2 items-center text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <FileText size={12} className="text-primary" />
          <span>Challan: <span className="font-mono font-semibold text-foreground">{challanNo}</span></span>
        </div>
        <div className="flex items-center gap-2">
          <Clock size={12} />
          <span>{dispatchedAt}</span>
        </div>
        <div className="flex items-center gap-2">
          <MapPin size={12} />
          <span>{kit.destination}</span>
        </div>
      </div>
      <div className="flex gap-3">
        <button
          onClick={onViewChallan}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          <FileText size={15} />
          View Challan
        </button>
        <button
          onClick={onScanNext}
          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <Scan size={15} />
          Scan Next Kit
        </button>
      </div>
    </div>
  );
}

// ─── Main board ───────────────────────────────────────────────────────────────

export default function DispatchBoard() {
  const [scanInput, setScanInput] = useState('');
  const [kitRecord, setKitRecord] = useState<KitRecord | null>(null);
  const [scanState, setScanState] = useState<'idle' | 'found' | 'not_found' | 'dispatched'>('idle');
  const [flashClass, setFlashClass] = useState('');
  const [showChallan, setShowChallan] = useState(false);
  const [challanNo, setChallanNo] = useState('');
  const [dispatchedAt, setDispatchedAt] = useState('');
  const scanRef = useRef<HTMLInputElement>(null);

  const handleScan = (e: React.FormEvent) => {
    e.preventDefault();
    const val = scanInput.trim();
    if (!val) return;

    const found = KIT_DATABASE[val] ?? null;

    if (found) {
      setKitRecord(found);
      setScanState('found');
      setFlashClass('ring-2 ring-primary/60');
      setTimeout(() => setFlashClass(''), 1000);
    } else {
      setKitRecord(null);
      setScanState('not_found');
      setFlashClass('ring-2 ring-red-400/60');
      setTimeout(() => {
        setFlashClass('');
        setScanState('idle');
      }, 1500);
    }
    setScanInput('');
  };

  const handleReset = () => {
    setKitRecord(null);
    setScanState('idle');
    setScanInput('');
    setFlashClass('');
    setShowChallan(false);
    setTimeout(() => scanRef.current?.focus(), 50);
  };

  const handleDispatch = () => {
    if (!kitRecord) return;
    const now = new Date();
    const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    const dateStr = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const ts = `${timeStr}, ${dateStr}`;
    const cn = `DCH-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
    setChallanNo(cn);
    setDispatchedAt(ts);
    setScanState('dispatched');
  };

  const hasAlert = kitRecord?.childItems.some((i) => i.status === 'expiring' || i.status === 'expired');

  return (
    <div className="flex flex-col gap-4 flex-1 overflow-hidden">
      {/* Scan bar — always visible unless dispatched */}
      {scanState !== 'dispatched' && (
        <form onSubmit={handleScan} className="flex gap-3">
          <div className={`flex-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border transition-all duration-300 ${flashClass}`}>
            <Scan size={18} className="text-primary flex-shrink-0" />
            <input
              ref={scanRef}
              type="text"
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              placeholder="Scan completed kit barcode (e.g. KIT-20260612-001)"
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
              autoFocus
            />
            {scanInput && (
              <button type="submit" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors">
                <Search size={12} />
                Lookup
              </button>
            )}
          </div>
          {kitRecord && (
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <RotateCcw size={14} />
              Clear
            </button>
          )}
        </form>
      )}

      {/* Not found state */}
      {scanState === 'not_found' && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20">
          <AlertTriangle size={16} className="text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-400 font-medium">Kit barcode not found. Please verify and scan again.</p>
        </div>
      )}

      {/* Idle / empty state */}
      {scanState === 'idle' && !kitRecord && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Truck size={28} className="text-primary" />
          </div>
          <div>
            <p className="text-base font-semibold text-foreground">Scan a Completed Kit</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-xs">
              Scan a completed kit barcode to review the item manifest, generate a dispatch challan, and mark the kit as Dispatched.
            </p>
          </div>
          <div className="flex flex-col gap-1.5 text-xs text-muted-foreground">
            <p className="font-medium text-foreground/60">Try demo barcodes:</p>
            {Object.keys(KIT_DATABASE).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => { setScanInput(k); setTimeout(() => scanRef.current?.focus(), 50); }}
                className="px-3 py-1 rounded-lg bg-muted hover:bg-muted/80 text-xs font-mono text-foreground transition-colors"
              >
                {k}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Dispatched success state */}
      {scanState === 'dispatched' && kitRecord && (
        <DispatchSuccess
          kit={kitRecord}
          challanNo={challanNo}
          dispatchedAt={dispatchedAt}
          onViewChallan={() => setShowChallan(true)}
          onScanNext={handleReset}
        />
      )}

      {/* Kit record found — manifest + dispatch action */}
      {scanState === 'found' && kitRecord && (
        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Expiry alert banner */}
          {hasAlert && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-accent/10 border border-accent/30">
              <AlertTriangle size={16} className="text-accent flex-shrink-0" />
              <p className="text-sm text-accent font-medium">
                ⚠ This kit contains items expiring soon — verify before dispatch.
              </p>
            </div>
          )}

          {/* Kit summary card */}
          <div className="rounded-xl bg-card border border-border p-4">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Layers size={20} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-foreground">{kitRecord.kitName}</h2>
                  <p className="text-xs font-mono text-muted-foreground mt-0.5">{kitRecord.kitId}</p>
                </div>
              </div>
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20">
                <CheckCircle2 size={12} />
                Ready for Dispatch
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="flex flex-col gap-1 px-3 py-2.5 rounded-lg bg-muted/40">
                <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
                  <User size={11} />
                  Operator
                </div>
                <p className="text-xs font-semibold text-foreground">{kitRecord.operator}</p>
                <p className="text-2xs text-muted-foreground">{kitRecord.operatorId} · {kitRecord.device}</p>
              </div>
              <div className="flex flex-col gap-1 px-3 py-2.5 rounded-lg bg-muted/40">
                <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
                  <Clock size={11} />
                  Completed At
                </div>
                <p className="text-xs font-semibold text-foreground">{kitRecord.completedAt}</p>
                <p className="text-2xs text-muted-foreground">{kitRecord.completedDate}</p>
              </div>
              <div className="flex flex-col gap-1 px-3 py-2.5 rounded-lg bg-muted/40">
                <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
                  <Package size={11} />
                  Total Items
                </div>
                <p className="text-xs font-semibold text-foreground">{kitRecord.totalItems} components</p>
                <p className="text-2xs text-muted-foreground">All scanned &amp; verified</p>
              </div>
              <div className="flex flex-col gap-1 px-3 py-2.5 rounded-lg bg-muted/40">
                <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
                  <MapPin size={11} />
                  Destination
                </div>
                <p className="text-xs font-semibold text-foreground">{kitRecord.destination}</p>
                <p className="text-2xs text-muted-foreground">Dispatch target</p>
              </div>
            </div>
          </div>

          {/* Item manifest table */}
          <div className="rounded-xl bg-card border border-border overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Layers size={15} className="text-primary" />
                <span className="text-sm font-semibold text-foreground">Item Manifest</span>
                <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-2xs font-bold">{kitRecord.childItems.length}</span>
              </div>
              <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
                <ShieldCheck size={11} />
                Mfg &amp; expiry dates verified
              </div>
            </div>

            {/* Table header */}
            <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-muted/30 border-b border-border text-2xs font-semibold text-muted-foreground uppercase tracking-wide">
              <div className="col-span-3">Item Name</div>
              <div className="col-span-2">Batch No.</div>
              <div className="col-span-1 text-center">Qty</div>
              <div className="col-span-2">Mfg Date</div>
              <div className="col-span-2">Expiry Date</div>
              <div className="col-span-2">Shelf Life</div>
            </div>

            {/* Table rows */}
            <div className="divide-y divide-border">
              {kitRecord.childItems.map((item) => (
                <div
                  key={item.id}
                  className={`grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-muted/20 transition-colors ${
                    item.status === 'expiring' ? 'bg-accent/5' : item.status === 'expired' ? 'bg-red-500/5' : ''
                  }`}
                >
                  <div className="col-span-3">
                    <p className="text-xs font-semibold text-foreground leading-tight">{item.itemName}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-2xs text-muted-foreground font-mono">{item.grnId}</span>
                      <ChevronRight size={9} className="text-muted-foreground" />
                      <span className="text-2xs text-muted-foreground">{item.rackLocation}</span>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="flex items-center gap-1.5">
                      <Hash size={10} className="text-muted-foreground flex-shrink-0" />
                      <span className="text-xs font-mono text-foreground truncate">{item.batchNumber}</span>
                    </div>
                    <p className="text-2xs text-muted-foreground mt-0.5">{item.uom}</p>
                  </div>
                  <div className="col-span-1 text-center">
                    <span className="text-xs font-bold text-foreground">{item.quantity}</span>
                  </div>
                  <div className="col-span-2">
                    <div className="flex items-center gap-1.5">
                      <Calendar size={10} className="text-muted-foreground flex-shrink-0" />
                      <span className="text-xs text-foreground">{item.mfgDate}</span>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <span className={`text-xs font-medium ${item.status === 'expiring' ? 'text-accent' : item.status === 'expired' ? 'text-red-400' : 'text-foreground'}`}>
                      {item.expiryDate}
                    </span>
                    {item.status === 'expiring' && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <AlertTriangle size={9} className="text-accent" />
                        <span className="text-2xs text-accent">Expiring soon</span>
                      </div>
                    )}
                  </div>
                  <div className="col-span-2">
                    <ShelfLifeBar pct={item.shelfLifePct} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dispatch action bar */}
          <div className="rounded-xl bg-card border border-border p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Truck size={18} className="text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Ready to Dispatch</p>
                <p className="text-xs text-muted-foreground">Confirm to generate challan and mark kit as Dispatched</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setShowChallan(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <FileText size={14} />
                Preview Challan
              </button>
              <button
                onClick={handleDispatch}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
              >
                <Send size={15} />
                Mark as Dispatched
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Challan modal */}
      {showChallan && kitRecord && (
        <ChallanModal
          kit={kitRecord}
          challanNo={challanNo || `DCH-PREVIEW-${kitRecord.kitId}`}
          dispatchedAt={dispatchedAt || 'Preview — not yet dispatched'}
          onClose={() => setShowChallan(false)}
          onPrint={() => window.print()}
        />
      )}
    </div>
  );
}
